/*
 * <tn-notify-me> — back-in-stock sign-up on sold-out variants.
 * Markup and the why: snippets/tn-notify-me.liquid.
 *
 * Flow: number → POST {endpoint}/api/notify-me {action:'join'} → the dashboard
 * saves the sign-up and (usually) sends a WhatsApp code → customer types it →
 * {action:'verify'}. If no code could go out (verification: 'unavailable') or
 * the number was verified before ('verified'), we skip straight to "done":
 * the sign-up is saved either way.
 *
 * No framework, no imports: a plain deferred script like tn-quick-form.js, so
 * it works in the quick-add dialog as well as on the product page.
 */
(function () {
  if (customElements.get('tn-notify-me')) return;

  var PHONE_RE = /^[6-9]\d{9}$/;

  function digits(s) {
    var d = String(s || '').replace(/\D/g, '');
    if (d.length === 12 && d.indexOf('91') === 0) d = d.slice(2);
    if (d.length === 11 && d.charAt(0) === '0') d = d.slice(1);
    return d;
  }

  class TnNotifyMe extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.product = JSON.parse(this.querySelector('[data-tn-notify-product]').textContent);
      } catch (e) {
        this.hidden = true;
        return;
      }
      var endpoint = this.dataset.endpoint || '';
      try { this.origin = new URL(endpoint).origin; } catch (e) { this.origin = ''; }
      if (!this.origin) { this.hidden = true; return; }

      this.variantId = String(this.dataset.variantId || '');
      this.dialog = this.querySelector('dialog');
      this.steps = {};
      var self = this;
      this.querySelectorAll('[data-step]').forEach(function (el) { self.steps[el.dataset.step] = el; });

      this.querySelector('[data-tn-notify-open]').addEventListener('click', function () { self.open(); });
      this.querySelectorAll('[data-tn-notify-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.close(); });
      });
      this.dialog.addEventListener('click', function (e) { if (e.target === self.dialog) self.close(); });
      this.steps.phone.addEventListener('submit', function (e) { e.preventDefault(); self.join(); });
      this.steps.code.addEventListener('submit', function (e) { e.preventDefault(); self.verify(); });
      this.querySelector('[data-tn-notify-resend]').addEventListener('click', function () { self.resend(); });
      this.querySelector('[data-tn-notify-back]').addEventListener('click', function () { self.show('phone'); });
      // A stale "enter your number" under a number that is now fine reads as broken.
      this.dialog.addEventListener('input', function () { self.error(''); });

      // Horizon announces variant changes on the document (assets/events.js,
      // ThemeEvents.variantUpdate). Only react to our own product's picker.
      this._onVariant = function (e) {
        var d = e.detail || {};
        if (d.data && d.data.productId && String(d.data.productId) !== String(self.product.id)) return;
        var v = d.resource;
        self.variantId = v ? String(v.id) : '';
        self.hidden = !v || v.available !== false;
        if (self.hidden && self.dialog.open) self.close();
      };
      document.addEventListener('variant:update', this._onVariant);
    }

    disconnectedCallback() {
      if (this._onVariant) document.removeEventListener('variant:update', this._onVariant);
      clearInterval(this._timer);
    }

    variant() {
      var id = this.variantId;
      var list = this.product.variants || [];
      for (var i = 0; i < list.length; i++) if (String(list[i].id) === id) return list[i];
      return list[0] || null;
    }

    open() {
      var v = this.variant();
      this.querySelector('[data-tn-notify-product-label]').textContent =
        this.product.title + (v && v.title ? ' — ' + v.title : '');
      // A different variant from last time is a fresh sign-up.
      if (this.ref && this.refVariant !== this.variantId) this.ref = null;
      this.show(this.ref && !this.done ? 'code' : 'phone');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      var input = this.steps[this.current].querySelector('input');
      if (input) setTimeout(function () { input.focus(); }, 50);
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      if (this.done) { this.done = false; this.ref = null; }
    }

    show(step) {
      this.current = step;
      for (var k in this.steps) this.steps[k].hidden = k !== step;
      this.error('');
    }

    error(msg) {
      var el = this.steps[this.current] && this.steps[this.current].querySelector('[data-tn-notify-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    busy(on) {
      var btn = this.steps[this.current].querySelector('button[type="submit"]');
      if (btn) { btn.disabled = on; btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }

    post(body) {
      return fetch(this.origin + '/api/notify-me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || data.ok === false) throw new Error(data.error || 'Something went wrong. Please try again.');
          return data;
        });
      }, function () {
        throw new Error("Couldn't reach us. Check your connection and try again.");
      });
    }

    join() {
      var form = this.steps.phone;
      var phone = digits(form.elements.phone.value);
      if (!PHONE_RE.test(phone)) return this.error('Enter your 10-digit WhatsApp number.');
      var v = this.variant();
      if (!v) return this.error('Pick an option first.');

      var self = this;
      this.busy(true);
      this.error('');
      this.post({
        action: 'join',
        phone: phone,
        name: form.elements.name.value.trim() || null,
        product_id: this.product.id,
        product_handle: this.product.handle,
        product_title: this.product.title,
        product_url: this.product.url,
        variant_id: v.id,
        variant_title: v.title,
        sku: v.sku || null,
        image_url: v.image || this.product.image || null,
      }).then(function (data) {
        self.ref = data.ref;
        self.refVariant = String(v.id);
        self.masked = data.phone_masked || ('+91 ' + phone);
        self.afterSend(data);
      }).catch(function (err) {
        self.error(err.message);
      }).then(function () { self.busy(false); });
    }

    afterSend(data) {
      if (data.verification === 'sent') {
        this.show('code');
        this.querySelector('[data-tn-notify-phone]').textContent = this.masked;
        var code = this.steps.code.elements.code;
        code.value = '';
        setTimeout(function () { code.focus(); }, 50);
        this.cooldown(data.resend_after_sec || 60);
      } else {
        this.finish(data.verification === 'verified');
      }
    }

    verify() {
      var code = digits(this.steps.code.elements.code.value);
      if (!/^\d{6}$/.test(code)) return this.error('Enter the 6-digit code from WhatsApp.');
      var self = this;
      this.busy(true);
      this.post({ action: 'verify', ref: this.ref, code: code })
        .then(function () { self.finish(true); })
        .catch(function (err) { self.error(err.message); })
        .then(function () { self.busy(false); });
    }

    resend() {
      var self = this;
      var btn = this.querySelector('[data-tn-notify-resend]');
      btn.disabled = true;
      this.post({ action: 'resend', ref: this.ref })
        .then(function (data) {
          if (data.verification === 'sent') {
            self.error('');
            self.cooldown(data.resend_after_sec || 60);
          } else {
            self.finish(data.verification === 'verified');
          }
        })
        .catch(function (err) { self.error(err.message); btn.disabled = false; });
    }

    cooldown(sec) {
      var btn = this.querySelector('[data-tn-notify-resend]');
      var left = Math.max(1, Math.round(sec));
      clearInterval(this._timer);
      btn.disabled = true;
      btn.textContent = 'Send a new code (' + left + 's)';
      this._timer = setInterval(function () {
        left -= 1;
        if (left <= 0) {
          clearInterval(this._timer);
          btn.disabled = false;
          btn.textContent = 'Send a new code';
        } else {
          btn.textContent = 'Send a new code (' + left + 's)';
        }
      }.bind(this), 1000);
    }

    finish(verified) {
      clearInterval(this._timer);
      this.done = true;
      var v = this.variant();
      var what = this.product.title + (v && v.title ? ' (' + v.title + ')' : '');
      this.querySelector('[data-tn-notify-done-text]').textContent = verified
        ? "We'll message you on WhatsApp at " + this.masked + ' as soon as ' + what + ' is back.'
        : "We've noted " + this.masked + " and we'll let you know as soon as " + what + ' is back.';
      this.show('done');
    }
  }

  customElements.define('tn-notify-me', TnNotifyMe);
})();
